import styled from "@emotion/styled";
import { useState } from "react";
import OTPInput from "react-otp-input";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { PaartnerLogo } from "./assets";
import * as PRTNR from "./prtnr";
import {
  checkAuthStatus,
  loginUser,
  verifyTwoFactorCode,
} from "./redux/slices/authThunks";

const baseStyle = {
  width: "48px",
  height: "42px",
  fontSize: "18px",
  borderRadius: "8px",
  textAlign: "center",
  border: "1px solid rgb(224, 228, 244)",
  color: "rgb(33, 37, 67)",
  fontWeight: "600",
  background: "white",
};

const errorStyle = {
  ...baseStyle,
  border: "1px solid rgb(220, 38, 38)", // red border
};
const OtpBox = styled.input`
  min-width: 48px;
  min-height: 42px;
  text-align: center;
  font-size: 18px;
  border: 1px solid
    ${(props) => (props.error ? "rgb(220, 38, 38)" : "rgb(224, 228, 244)")};
  border-radius: 8px;
  font-family: Montserrat, sans-serif;
  font-weight: 600;
  color: rgb(33, 37, 67);
  background: white;
  transition: border-color 0.2s;
  margin-right: 10px;

  &:focus {
    border-color: rgb(97, 98, 197);
    outline: none;
  }

  &::placeholder {
    color: rgb(179, 182, 195);
  }
  @media (max-width: 480px) {
    min-width: 40px;
    min-height: 36px;
    font-size: 16px;
    margin-right: 8px;
  }
`;
export default function OTPAuthentication() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { userId } = useParams();

  const query = new URLSearchParams(location.search);
  const stateParam = query.get("s");

  const { email, password } = stateParam ? JSON.parse(atob(stateParam)) : {};

  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [resendDisabled, setResendDisabled] = useState(false);
  const [timer, setTimer] = useState(0);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (otp.length !== 6) {
      setOtpError("OTP must be exactly 6 digits.");
      return;
    }

    try {
      await dispatch(
        verifyTwoFactorCode({
          userId: userId,
          code: otp,
        })
      ).unwrap();

      await dispatch(checkAuthStatus());

      // ✅ 1. Restore post-login action (existing logic)
      const saved = localStorage.getItem("postLoginAction");
      if (saved) {
        const { action, vendorId } = JSON.parse(saved);
        localStorage.removeItem("postLoginAction");

        if (action === "favourite" && vendorId) {
          navigate(`/vendors?action=${action}&vendorId=${vendorId}`);
          return;
        }
      }

      // ✅ 2. Handle redirect from Auth app
      const params = new URLSearchParams(window.location.search);
      const encodedState = params.get("s");
      let redirectUrl = null;

      if (encodedState) {
        try {
          const decoded = JSON.parse(atob(encodedState));
          redirectUrl = decoded.redirect || null;
        } catch (err) {
          console.error("Failed to decode redirect param:", err);
        }
      }

      // fallback (if user refreshed)
      if (!redirectUrl) {
        redirectUrl = sessionStorage.getItem("redirectAfterAuth");
      }

      if (redirectUrl) {
        sessionStorage.removeItem("redirectAfterAuth");
        window.location.href = redirectUrl; // 🔁 go back to original deep link
        return;
      }

      // ✅ 3. Default redirect if nothing else
      navigate("/vendors");
    } catch (err) {
      setOtpError(err.message || "Invalid OTP. Please try again.");
    }
  };

  const ResendOTP = () => {
    if (resendDisabled) return;

    setResendDisabled(true);
    setTimer(15);

    dispatch(loginUser({ email, password }));
    toast.success("Reset Token Sent");

    // Countdown timer
    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setResendDisabled(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };
  return (
    <RootWrapperHome>
      <Header>
        <HeaderWrapper>
          <Left>
            <Logo
              onClick={() => navigate("/")}
              src={PaartnerLogo}
              alt="Paartner Logo"
            />
          </Left>
        </HeaderWrapper>
      </Header>
      <Section>
        <Wrapper>
          <SignupSignin>
            <ContentBlock1>
              <Content>
                <EarningCashForMakingReferralsIsEasierThanEver>
                  OTP Authentication
                </EarningCashForMakingReferralsIsEasierThanEver>
                <DiscoverPaartnerSTrustedAlliesReadyToHelpYourFriendsWithTheServicesAndMarTechTheyNeedToSucceed>
                  Enter the OTP we sent to your email.
                </DiscoverPaartnerSTrustedAlliesReadyToHelpYourFriendsWithTheServicesAndMarTechTheyNeedToSucceed>
              </Content>
            </ContentBlock1>
            <Frame60>
              <Frame58>
                <OTPInput
                  value={otp}
                  onChange={setOtp}
                  numInputs={6}
                  inputType="tel"
                  renderInput={(props) => <OtpBox {...props} />}
                  inputStyle={otpError ? errorStyle : baseStyle}
                />
                {otpError && <ErrorMessage>{otpError}</ErrorMessage>}

                <Frame59>
                  <ButtonWrapper>
                    <PRTNR.Button variant="black" onClick={handleSubmit}>
                      Login
                    </PRTNR.Button>
                  </ButtonWrapper>
                  <PRTNR.Button variant="gray-light">
                    <span style={{ fontWeight: 500, fontSize: "16px" }}>
                      Didn't receive an OTP?{" "}
                      <span
                        onClick={!resendDisabled ? ResendOTP : undefined}
                        style={{
                          color: "#6162C5",
                          fontWeight: 600,
                          cursor: resendDisabled ? "not-allowed" : "pointer",
                          pointerEvents: resendDisabled ? "none" : "auto",
                        }}
                      >
                        {resendDisabled ? `Resend in ${timer}s` : "Resend"}
                      </span>
                    </span>
                  </PRTNR.Button>
                </Frame59>
              </Frame58>
              <Hint>
                Hint: you might need to check your spam or junk folders if it's
                not in your inbox.
              </Hint>
            </Frame60>
          </SignupSignin>
        </Wrapper>
      </Section>
    </RootWrapperHome>
  );
}

const RootWrapperHome = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: center;
  /* min-height: 100vh; */
  background: rgb(255, 255, 255);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
`;

const Header = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: row;
  align-items: center;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;
const Hint = styled.div`
  font-family: Montserrat;
  font-weight: 500;
  font-size: 16px;
  line-height: 160%;
  letter-spacing: 0%;
  text-align: center;
  vertical-align: middle;
  color: var(--Color-Text-Gray, #64748b);
`;

const HeaderWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: center;
  flex: 1;
  border-radius: 12px;
  box-sizing: border-box;
  padding: 24px 16px;
`;

const Left = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  flex: none;
  gap: 8px;
  box-sizing: border-box;
`;

const Logo = styled.img`
  width: 165px;
  height: 24px;
  position: relative;
`;

const Section = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  flex: 1;
  gap: 8px;
  align-self: stretch;
  height: 882px;
  background: rgb(241, 245, 249);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
  padding: clamp(1rem, 5vw, 3.75rem);
  flex-shrink: 0;
`;

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  flex: none;
  box-sizing: border-box;
`;

const SignupSignin = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const ContentBlock1 = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: row;
  align-items: center;
  gap: 32px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const Content = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: center;
  flex: 1;
  width: 560px;
  box-sizing: border-box;
  @media (max-width: 1024px) {
    width: 100%;
  }

  @media (max-width: 600px) {
    width: 100%;
  }
`;

const EarningCashForMakingReferralsIsEasierThanEver = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: clamp(24px, 2.5vw, 48px);
  font-family: Montserrat, sans-serif;
  font-weight: 600;
  letter-spacing: -1px;
  text-align: center;
  align-self: stretch;
  flex-shrink: 0;
`;

const DiscoverPaartnerSTrustedAlliesReadyToHelpYourFriendsWithTheServicesAndMarTechTheyNeedToSucceed = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: clamp(16px, 2vw, 20px);
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: center;
  align-self: stretch;
  flex-shrink: 0;
`;
const ErrorMessage = styled.span`
  color: rgb(220, 38, 38);
  font-size: 12px;
  font-family: Montserrat, sans-serif;
  margin-top: 4px;
  text-align: center;
`;
const ButtonWrapper = styled.span`
  width: 320px;
  @media (max-width: 1024px) {
    width: 100%;
  }

  @media (max-width: 600px) {
    width: 100%;
  }
`;
const Frame58 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: center;
  flex: none;
  gap: 24px;
  border: solid 1px rgba(136, 136, 136, 0.1);
  border-radius: 32px;
  width: 560px;
  background: rgb(226, 232, 240);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;

  padding: clamp(16px, 2vw, 32px);
  @media (max-width: 1024px) {
    width: 100%;
  }

  @media (max-width: 600px) {
    width: 100%;
  }
`;
const Frame60 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: center;
  gap: 32px;

  width: 560px;
  @media (max-width: 1024px) {
    width: 100%;
  }

  @media (max-width: 600px) {
    width: 100%;
  }
`;

const Frame59 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;
